import React from 'react';
import { Skeleton } from './Skeleton';
import { SkeletonStatsGrid } from './SkeletonStats';
import { SkeletonChart } from './SkeletonChart';
import { SkeletonTable } from './SkeletonTable';

/**
 * Skeleton Page Header - for page headers with title and actions
 */
export const SkeletonPageHeader = ({
  hasActions = true,
  hasSearch = false,
  className = ''
}) => {
  return (
    <div
      className={`skeleton-page-header ${className}`}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}
    >
      <div style={{ flex: 1 }}>
        <Skeleton width="200px" height="2rem" style={{ marginBottom: '0.5rem' }} />
        <Skeleton width="300px" height="1rem" />
      </div>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        {hasSearch && (
          <Skeleton width="250px" height="42px" borderRadius="6px" />
        )}
        {hasActions && (
          <>
            <Skeleton width="100px" height="42px" borderRadius="6px" />
            <Skeleton width="120px" height="42px" borderRadius="6px" />
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Skeleton Dashboard Page - complete dashboard layout
 */
export const SkeletonDashboardPage = ({ className = '' }) => {
  return (
    <div className={`skeleton-dashboard-page ${className}`}>
      <SkeletonPageHeader hasActions={true} hasSearch={false} />

      {/* Stats Grid */}
      <SkeletonStatsGrid count={4} style={{ marginBottom: '2rem' }} />

      {/* Charts Row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}
      >
        <SkeletonChart type="bar" height="300px" />
        <SkeletonChart type="line" height="300px" />
      </div>

      {/* Recent Activity Table */}
      <div
        style={{
          backgroundColor: 'var(--color-surface, #fff)',
          padding: '1.5rem',
          borderRadius: '0.75rem',
          border: '1px solid #e2e8f0'
        }}
      >
        <Skeleton width="200px" height="1.5rem" style={{ marginBottom: '1.5rem' }} />
        <SkeletonTable rows={5} columns={4} hasActions={true} />
      </div>
    </div>
  );
};

/**
 * Skeleton List Page - for pages with tables/lists
 */
export const SkeletonListPage = ({
  hasStats = false,
  className = ''
}) => {
  return (
    <div className={`skeleton-list-page ${className}`}>
      <SkeletonPageHeader hasActions={true} hasSearch={true} />

      {hasStats && (
        <SkeletonStatsGrid count={3} style={{ marginBottom: '2rem' }} />
      )}

      <div
        style={{
          backgroundColor: 'var(--color-surface, #fff)',
          padding: '1.5rem',
          borderRadius: '0.75rem',
          border: '1px solid #e2e8f0'
        }}
      >
        <SkeletonTable rows={8} columns={5} hasActions={true} />
      </div>
    </div>
  );
};

/**
 * Skeleton Details Page - for detail/edit pages
 */
export const SkeletonDetailsPage = ({ className = '' }) => {
  return (
    <div className={`skeleton-details-page ${className}`}>
      <SkeletonPageHeader hasActions={true} hasSearch={false} />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
        {/* Main Content */}
        <div>
          <div
            style={{
              backgroundColor: 'var(--color-surface, #fff)',
              padding: '2rem',
              borderRadius: '0.75rem',
              border: '1px solid #e2e8f0',
              marginBottom: '1.5rem'
            }}
          >
            <Skeleton width="40%" height="1.75rem" style={{ marginBottom: '1.5rem' }} />
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} style={{ marginBottom: '1.5rem' }}>
                <Skeleton width="30%" height="1rem" style={{ marginBottom: '0.5rem' }} />
                <Skeleton width="100%" height="42px" borderRadius="6px" />
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div
            style={{
              backgroundColor: 'var(--color-surface, #fff)',
              padding: '1.5rem',
              borderRadius: '0.75rem',
              border: '1px solid #e2e8f0'
            }}
          >
            <Skeleton width="60%" height="1.5rem" style={{ marginBottom: '1rem' }} />
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} style={{ marginBottom: '1rem' }}>
                <Skeleton width="100%" height="1rem" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkeletonPageHeader;
